import { useAtom } from "jotai";
import toast from "react-hot-toast";
import { formatCurrency } from "utils/formatters";
import CrmDashboardSkeleton from "components/skeletons/CrmDashboardSkeleton";
import {Helmet} from "react-helmet";
import axiosInstance from "helpers/api";
import { useCookies } from "react-cookie";
import { Col, Row, Badge, Card, Button, ProgressBar } from "react-bootstrap";
import { useEffect, useState } from "react";
// import "./MobileNavFix"; // Import mobile navigation fix for small screens
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import LeadSources from "components/list-items/LeadSources";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { aiChatTrigger } from "utils/aiChatTrigger";
import { 
  faHandshake, 
  faUser, 
  faRobot,
  faChartLine,
  faBrain,
  faDollarSign,
  faPhone,
  faComments,
  faMapMarkerAlt,
  faCalendarAlt,
  faArrowTrendUp,
  faClock,
  faUserTie,
  faTachometerAlt,
  faWaveSquare,
  faLightbulb,
  faFire,
  faThumbsUp,
  faBullseye
} from "@fortawesome/free-solid-svg-icons";
import './Crm.css';
import './DashboardResponsive.css';
import { UserInfo } from "interfaces/user";

// TypeScript interfaces - ChatMessage interface moved to GlobalDashboardAI component

const Crm = () => {
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Start with loading state true
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cookie, setCookie, removeCookie] = useCookies([
    "isLoggedInKIQ",
    "stripeIdpKey",
  ]);

  useEffect(() => {
    removeCookie("stripeIdpKey", { path: "/" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeadData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/dashboard/leads");

      // Add proper validation for response structure
      if (response && response.data) {
        // Backend now returns { success: true, data: { leads: [...], pagination: {...} } }
        const leadsData = response.data.data?.leads || response.data.leads || response.data.data || response.data || [];
        setLeads(leadsData);
        console.log(`✅ Fetched ${leadsData.length} leads`);
      } else {
        console.warn('No data received from leads API');
        setLeads([]);
      }
    } catch (error: any) {
      console.error('Error fetching lead data:', error);
      setLeads([]);
      
      // Only show toast if we have error details
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to fetch lead data');
      }
    }
  };

  const fetchDealData = async () => {
    try {
      const response = await axiosInstance.get("/dashboard/deals");
      
      // Add proper validation for response structure  
      if (response && response.data) {
        // Our API returns { success: true, deals: [...], count: N }
        setDeals(response.data.deals || response.data.data || response.data || []);
      } else {
        console.warn('No data received from deals API');
        setDeals([]);
      }
    } catch (error: any) {
      console.error('Error fetching deal data:', error);
      setDeals([]);
      
      // Only show toast if we have error details
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to fetch deal data');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchLeadData(),
        fetchDealData()
      ]);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const totalPrice = deals && Array.isArray(deals) 
    ? deals.reduce((sum, item: any) => {
        const price = typeof item?.deal_price === 'number' ? item.deal_price : 0;
        return sum + price;
      }, 0)
    : 0;

  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showCrm, setShowCrm] = useState(true);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowCrm(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  // Real-time analytics calculated from actual data
  const [analytics, setAnalytics] = useState({
    avgResponseTime: "Calculating...",
    conversionRate: "0%",
    activeSmsCampaigns: 0,
    monthlyRoi: "0%",
    appointmentsToday: 0,
    hotLeads: 0
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Calculate analytics from real data
  useEffect(() => {
    const calculateAnalytics = () => {
      if (!leads || !deals) return;

      // Calculate hot leads (leads created in last 7 days with high engagement)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const hotLeadsCount = leads.filter((lead: any) => {
        const createdDate = new Date(lead.created_on);
        return createdDate > sevenDaysAgo && (lead.lead_score > 70 || lead.priority_level === 'high');
      }).length;

      // Calculate conversion rate (deals / leads)
      const conversionRate = leads.length > 0 
        ? Math.round((deals.length / leads.length) * 100) 
        : 0;

      // Calculate appointments today (temporary - will come from backend)
      // const today = new Date().toDateString();
      const appointmentsToday = 0; // TODO: Fetch from appointments API

      // Calculate average response time (temporary placeholder)
      const avgResponseTime = "< 1 hour"; // TODO: Calculate from actual response data

      // Calculate monthly ROI (temporary calculation)
      const monthlyRoi = totalPrice > 0 ? "Positive" : "0%";

      // Active SMS campaigns (temporary - will come from backend)
      const activeSmsCampaigns = 0; // TODO: Fetch from SMS campaigns API

      setAnalytics({
        avgResponseTime,
        conversionRate: `${conversionRate}%`,
        activeSmsCampaigns,
        monthlyRoi,
        appointmentsToday,
        hotLeads: hotLeadsCount
      });
      setAnalyticsLoading(false);
    };

    calculateAnalytics();
  }, [leads, deals, totalPrice]);

  // AI Chatbot trigger handlers for dashboard buttons
  const handleViewDetailedAnalysis = () => {
    console.log('🔍 [DASHBOARD] View Detailed Analysis button clicked');
    aiChatTrigger.triggerDetailedAnalysis(leads, deals, analytics);
  };

  const handleSetupAutoActions = () => {
    console.log('🔍 [DASHBOARD] Setup Auto Actions button clicked');
    aiChatTrigger.triggerAutoActionsSetup(leads, deals, analytics);
  };

  const handleOptimizeBudgetAllocation = () => {
    console.log('🔍 [DASHBOARD] Optimize Budget Allocation button clicked');
    aiChatTrigger.triggerBudgetOptimization(leads, deals, analytics, totalPrice);
  };

  // AI Insights panel trigger handlers
  const handleMarketAnalysis = () => {
    console.log('🔍 [DASHBOARD] Market Analysis insight clicked', { leads: leads?.length, deals: deals?.length, analytics, totalPrice });
    try {
      aiChatTrigger.triggerMarketAnalysis(leads, deals, analytics, totalPrice);
      console.log('✅ [DASHBOARD] Market Analysis trigger sent successfully');
    } catch (error) {
      console.error('❌ [DASHBOARD] Market Analysis trigger failed:', error);
    }
  };

  const handleOpportunityDetection = () => {
    console.log('🔍 [DASHBOARD] Opportunity Detection insight clicked', { leads: leads?.length, deals: deals?.length, analytics });
    try {
      aiChatTrigger.triggerOpportunityDetection(leads, deals, analytics);
      console.log('✅ [DASHBOARD] Opportunity Detection trigger sent successfully');
    } catch (error) {
      console.error('❌ [DASHBOARD] Opportunity Detection trigger failed:', error);
    }
  };

  const handleSystemIntegration = () => {
    console.log('🔍 [DASHBOARD] System Integration insight clicked', { leads: leads?.length, deals: deals?.length, analytics });
    try {
      aiChatTrigger.triggerSystemIntegration(leads, deals, analytics);
      console.log('✅ [DASHBOARD] System Integration trigger sent successfully');
    } catch (error) {
      console.error('❌ [DASHBOARD] System Integration trigger failed:', error);
    }
  };

  // Personalized greeting logic
  const getPersonalizedGreeting = () => {
    const hour = new Date().getHours();
    const firstName = userInfo?.user_first_name?.split(' ')[0] || userInfo?.user_email?.split('@')[0] || 'there';
    
    let timeOfDay = '';
    if (hour < 12) timeOfDay = 'Good morning';
    else if (hour < 17) timeOfDay = 'Good afternoon';
    else timeOfDay = 'Good evening';
    
    const greetings = [
      `${timeOfDay}, ${firstName}! Ready to close some deals today?`,
      `Hi ${firstName}! Let's maximize your lead potential today.`,
      `${timeOfDay}, ${firstName}! Your AI assistant is ready to help.`,
      `Welcome back, ${firstName}! Let's grow your business together.`,
      `${timeOfDay}, ${firstName}! Time to turn leads into closings.`
    ];
    
    return greetings[Math.floor(Math.random() * greetings.length)];
  };

  // AI Chatbot state - Now handled by GlobalDashboardAI component
  // Removed local chatbot implementation in favor of global AI assistant

  // AI Chatbot functionality moved to GlobalDashboardAI component



  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>KairosIQ.ai - Real Estate Intelligence Dashboard</title>
      </Helmet>
      {(isAuthorized || showCrm) && (
        isLoading ? <CrmDashboardSkeleton /> : (
        <div className="enhanced-crm-dashboard">
          {/* Enhanced Header with Quick Actions */}
          <div className="dashboard-header-enhanced">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <h1 className="dashboard-title-enhanced m-0">
                    <FontAwesomeIcon icon={faTachometerAlt} className="me-3" style={{ color: '#00d4ff', filter: 'drop-shadow(0 0 12px rgba(0, 212, 255, 0.6))' }} />
                    Real Estate Intelligence Hub
                  </h1>
                  <div className="d-flex gap-2">
                    <Badge className="ai-badge-enhanced">
                    <FontAwesomeIcon icon={faBrain} className="me-1" />
                    AI-POWERED
                  </Badge>
                  <Badge className="production-badge">
                    <FontAwesomeIcon icon={faFire} className="me-1" />
                    LIVE
                  </Badge>
                  </div>
                </div>
                <p className="dashboard-subtitle-enhanced mb-2">
                  <FontAwesomeIcon icon={faWaveSquare} className="me-2" style={{ color: '#00d4ff' }} />
                  Advanced workflow orchestration with ML-powered insights
                </p>
                <div className="ai-greeting-card">
                  <div className="d-flex align-items-center">
                    <div className="ai-avatar me-3">
                      <FontAwesomeIcon icon={faRobot} />
                    </div>
                    <div className="greeting-text">
                      <p className="mb-0">{getPersonalizedGreeting()}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="quick-actions-panel">
                <Button variant="primary" className="action-btn me-2">
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  SMS Campaign
                </Button>
                <Button variant="outline-primary" className="action-btn">
                  <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                  AI Insights
                </Button>
              </div>
            </div>
          </div>

          {/* Enhanced Metrics Grid */}
          <Row className="g-4 mb-4">
            {/* Primary Metrics */}
            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced leads-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container leads-icon">
                      <FontAwesomeIcon icon={faUserTie} size="lg" />
                    </div>
                    <div className="metric-status active-status">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
                      <span>Growing</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{leads?.length || 0}</div>
                  <div className="metric-label-enhanced">Active Leads</div>
                  <div className="metric-detail">
                                         <FontAwesomeIcon icon={faBullseye} className="me-2" />
                    {analyticsLoading ? "Loading..." : `${analytics.hotLeads} hot prospects`}
                  </div>
                  <ProgressBar now={75} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced deals-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container deals-icon">
                      <FontAwesomeIcon icon={faHandshake} size="lg" />
                    </div>
                    <div className="metric-status success-status">
                      <FontAwesomeIcon icon={faThumbsUp} className="me-1" />
                      <span>Excellent</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{deals?.length || 0}</div>
                  <div className="metric-label-enhanced">Active Deals</div>
                  <div className="metric-detail">
                    <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                    {formatCurrency(totalPrice)} total value
                  </div>
                  <ProgressBar now={60} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced conversion-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container conversion-icon">
                      <FontAwesomeIcon icon={faChartLine} size="lg" />
                    </div>
                    <div className="metric-status warning-status">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      <span>Tracking</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{analyticsLoading ? "..." : analytics.conversionRate}</div>
                  <div className="metric-label-enhanced">Conversion Rate</div>
                  <div className="metric-detail">
                    <FontAwesomeIcon icon={faComments} className="me-2" />
                    {analyticsLoading ? "Loading..." : `${analytics.avgResponseTime} avg response`}
                  </div>
                  <ProgressBar now={24} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Card className="metric-card-enhanced communication-card">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="metric-icon-container communication-icon">
                      <FontAwesomeIcon icon={faPhone} size="lg" />
                    </div>
                    <div className="metric-status active-status">
                      <FontAwesomeIcon icon={faRobot} className="me-1" />
                      <span>Automated</span>
                    </div>
                  </div>
                  <div className="metric-value-enhanced">{analyticsLoading ? "0" : analytics.activeSmsCampaigns}</div>
                  <div className="metric-label-enhanced">SMS Campaigns</div>
                  <div className="metric-detail">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    {analyticsLoading ? "Loading..." : `${analytics.appointmentsToday} appointments today`}
                  </div>
                  <ProgressBar now={85} className="metric-progress mt-2" />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Real-Time AI Recommendations */}
          <Row className="g-4 mb-4">
            <Col xl={12}>
              <div 
                className="ai-recommendations-section"
                style={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  backdropFilter: 'blur(15px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon 
                    icon={faBrain} 
                    style={{ 
                      color: '#f59e0b', 
                      fontSize: '24px',
                      marginRight: '12px',
                      filter: 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.5))',
                      animation: 'pulse 3s infinite'
                    }} 
                  />
                  <h5 
                    className="mb-0 fw-bold"
                    style={{ 
                      color: '#1f2937',
                      textShadow: '0 2px 10px rgba(245, 158, 11, 0.2)'
                    }}
                  >
                    🔥 Real-Time AI Recommendations
                  </h5>
                  <div 
                    className="ms-auto"
                    style={{
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      border: '1px solid rgba(245, 158, 11, 0.3)'
                    }}
                  >
                    <span style={{ color: '#f59e0b', fontSize: '12px', fontWeight: 600 }}>
                      LIVE ANALYSIS
                    </span>
                  </div>
                </div>

                <div 
                  className="recommendations-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '1rem'
                  }}
                >
                  {/* Priority Action Recommendation */}
                  <div 
                    className="recommendation-card priority"
                    style={{
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faFire} 
                        style={{ 
                          color: '#ef4444', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          🚨 Priority Action Required
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          {analyticsLoading 
                            ? "Analyzing your lead data..."
                            : leads?.length === 0 
                              ? "No active leads detected. Start by importing leads or launching a new campaign to build your pipeline."
                              : (leads?.length || 0) < 5 
                                ? `Low lead volume with ${leads?.length || 0} leads. ${analytics.hotLeads > 0 ? `Focus on your ${analytics.hotLeads} hot prospects first.` : 'Prioritize immediate follow-ups to increase engagement.'}`
                                : `Strong pipeline with ${leads?.length || 0} active leads! ${analytics.hotLeads > 0 ? `You have ${analytics.hotLeads} hot prospects ready for immediate action.` : 'Keep nurturing your leads for optimal conversions.'}`
                          }
                        </p>
                        <span style={{ 
                          color: '#ef4444', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Act within 2 hours
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Lead Source Optimization */}
                  <div 
                    className="recommendation-card optimization"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faChartLine} 
                        style={{ 
                          color: '#3b82f6', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          📈 Lead Source Optimization
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          {analyticsLoading
                            ? "Calculating lead source performance..."
                            : leads?.length > 0
                              ? `Your current ${analytics.conversionRate} conversion rate ${parseInt(analytics.conversionRate) > 20 ? 'is above industry average!' : 'has room for improvement.'} Focus on your highest-performing lead sources.`
                              : "Start tracking lead sources to identify your most profitable channels."
                          }
                        </p>
                        <span style={{ 
                          color: '#3b82f6', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Potential +25% ROI
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* AI Workflow Suggestion */}
                  <div 
                    className="recommendation-card workflow"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faRobot} 
                        style={{ 
                          color: '#10b981', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          🤖 Smart Workflow Suggestion
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          {analyticsLoading
                            ? "Analyzing workflow opportunities..."
                            : leads?.length > 10
                              ? `With ${leads.length} active leads, enable AI Auto-Nurture to automatically engage ${Math.floor((leads?.length || 0) * 0.3)} leads with personalized follow-ups.`
                              : deals?.length > 0
                                ? `Focus on closing your ${deals.length} active deals. Set up automated follow-ups to maintain momentum.`
                                : "Start with automated SMS campaigns to engage new leads immediately upon capture."
                          }
                        </p>
                        <span style={{ 
                          color: '#10b981', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Save 5 hours/week
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Market Intelligence */}
                  <div 
                    className="recommendation-card intelligence"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon 
                        icon={faLightbulb} 
                        style={{ 
                          color: '#8b5cf6', 
                          fontSize: '16px',
                          marginRight: '12px',
                          marginTop: '2px'
                        }} 
                      />
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '8px' }}>
                          🏠 Market Intelligence
                        </h6>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>
                          {analyticsLoading
                            ? "Gathering market intelligence..."
                            : totalPrice > 0
                              ? `Your deal pipeline totals ${formatCurrency(totalPrice)}. ${totalPrice > 1000000 ? 'Excellent high-value focus!' : 'Consider targeting higher-value properties for increased commissions.'}`
                              : "Connect with MLS data to receive real-time market intelligence and pricing insights."
                          }
                        </p>
                        <span style={{ 
                          color: '#8b5cf6', 
                          fontSize: '12px', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Market opportunity
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Center */}
                <div 
                  className="ai-action-center mt-3"
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleViewDetailedAnalysis}
                    disabled={analyticsLoading}
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 16px'
                    }}
                  >
                    📊 View Detailed Analysis
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleSetupAutoActions}
                    disabled={analyticsLoading}
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 16px'
                    }}
                  >
                    🤖 Setup AI Auto-Actions
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleOptimizeBudgetAllocation}
                    disabled={analyticsLoading}
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 16px'
                    }}
                  >
                    📈 Optimize Budget Allocation
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Workflow Orchestration Panel */}
          <Row className="g-4 mb-4">
            <Col xl={8}>
              <Card className="workflow-panel-enhanced">
                <Card.Header className="workflow-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="mb-1">
                        <FontAwesomeIcon icon={faWaveSquare} className="me-2" style={{ color: '#00d4ff' }} />
                        Lead Workflow Orchestration
                      </h5>
                      <p className="text-muted mb-0">AI-powered automation pipeline</p>
                    </div>
                    <Button variant="outline-primary" size="sm">
                      <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                      Optimize
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <div className="workflow-stages">
                    <div className="stage-item active">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div className="stage-content">
                        <h6>Lead Capture</h6>
                        <p className="text-muted">AI-powered lead qualification</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">+{leads?.length || 0} this week</span>
                        </div>
                      </div>
                    </div>
                    <div className="stage-connector"></div>
                    <div className="stage-item active">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faComments} />
                      </div>
                      <div className="stage-content">
                        <h6>AI Engagement</h6>
                        <p className="text-muted">Automated SMS & Email sequences</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">{analyticsLoading ? "Loading..." : `${analytics.avgResponseTime} response`}</span>
                        </div>
                      </div>
                    </div>
                    <div className="stage-connector"></div>
                    <div className="stage-item">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </div>
                      <div className="stage-content">
                        <h6>CMA Generation</h6>
                        <p className="text-muted">Advanced ML predictions</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">92% accuracy</span>
                        </div>
                      </div>
                    </div>
                    <div className="stage-connector"></div>
                    <div className="stage-item">
                      <div className="stage-icon">
                        <FontAwesomeIcon icon={faHandshake} />
                      </div>
                      <div className="stage-content">
                        <h6>Deal Closing</h6>
                        <p className="text-muted">Automated appointment scheduling</p>
                        <div className="stage-metrics">
                          <span className="metric-badge">{analyticsLoading ? "Loading..." : `${analytics.conversionRate} conversion`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xl={4}>
              <Card className="ai-insights-panel">
                <Card.Header>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: '#00d4ff' }} />
                    AI Insights
                  </h5>
                  <small className="text-muted d-block mt-1">Click insights for detailed AI analysis</small>
                </Card.Header>
                <Card.Body>
                  <div 
                    className="insight-item clickable-insight" 
                    onClick={(e) => {
                      console.log('🟢 [DEBUG] Market Analysis div clicked - event fired!', e);
                      handleMarketAnalysis();
                    }}
                    onMouseDown={() => console.log('🟢 [DEBUG] Market Analysis mouse down')}
                    onMouseUp={() => console.log('🟢 [DEBUG] Market Analysis mouse up')}
                    style={{ 
                      cursor: 'pointer !important', 
                      transition: 'all 0.2s ease',
                      padding: '12px',
                      borderRadius: '8px',
                      margin: '8px 0',
                      position: 'relative',
                      zIndex: 999,
                      userSelect: 'none',
                      pointerEvents: 'auto',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}
                    onMouseEnter={(e) => {
                      console.log('🟢 [DEBUG] Market Analysis hover enter');
                      e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      console.log('🟢 [DEBUG] Market Analysis hover leave');
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="insight-icon" style={{ pointerEvents: 'none' }}>
                      <FontAwesomeIcon icon={faFire} />
                    </div>
                    <div className="insight-content" style={{ pointerEvents: 'none' }}>
                      <h6>Hot Market Analysis</h6>
                      <p className="text-muted">
                        {analyticsLoading 
                          ? "Analyzing market conditions..." 
                          : totalPrice > 1000000 
                            ? `Strong pipeline positioning - $${(totalPrice/1000000).toFixed(1)}M in deals`
                            : leads?.length > 20 
                              ? "High lead volume - market opportunities detected"
                              : "Click for comprehensive market analysis"
                        }
                      </p>
                    </div>
                  </div>
                  <div 
                    className="insight-item clickable-insight" 
                    onClick={(e) => {
                      console.log('🟡 [DEBUG] Opportunity Detection div clicked - event fired!', e);
                      handleOpportunityDetection();
                    }}
                    style={{ 
                      cursor: 'pointer !important', 
                      transition: 'all 0.2s ease',
                      padding: '12px',
                      borderRadius: '8px',
                      margin: '8px 0',
                      position: 'relative',
                      zIndex: 999,
                      userSelect: 'none',
                      pointerEvents: 'auto',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="insight-icon" style={{ pointerEvents: 'none' }}>
                      <FontAwesomeIcon icon={faBullseye} />
                    </div>
                    <div className="insight-content" style={{ pointerEvents: 'none' }}>
                      <h6>Opportunity Detection</h6>
                      <p className="text-muted">
                        {analyticsLoading 
                          ? "Scanning for opportunities..."
                          : analytics.hotLeads > 0 
                            ? `${analytics.hotLeads} hot prospects ready for immediate action`
                            : leads?.length > 0
                              ? `${leads.length} leads analyzed - opportunities available`
                              : "Click to discover hidden opportunities"
                        }
                      </p>
                    </div>
                  </div>
                  <div 
                    className="insight-item clickable-insight" 
                    onClick={(e) => {
                      console.log('🔵 [DEBUG] System Integration div clicked - event fired!', e);
                      handleSystemIntegration();
                    }}
                    style={{ 
                      cursor: 'pointer !important', 
                      transition: 'all 0.2s ease',
                      padding: '12px',
                      borderRadius: '8px',
                      margin: '8px 0',
                      position: 'relative',
                      zIndex: 999,
                      userSelect: 'none',
                      pointerEvents: 'auto',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="insight-icon" style={{ pointerEvents: 'none' }}>
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <div className="insight-content" style={{ pointerEvents: 'none' }}>
                      <h6>System Integration</h6>
                      <p className="text-muted">
                        {analyticsLoading 
                          ? "Checking integrations..."
                          : analytics.activeSmsCampaigns > 0
                            ? `${analytics.activeSmsCampaigns} SMS campaigns active - optimization ready`
                            : analytics.avgResponseTime.includes('hour')
                              ? "Response time optimization available"
                              : "Click for integration and workflow optimization"
                        }
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* AI Lead Source Intelligence */}
          <Row className="g-4">
            <Col xl={12}>
              <Card className="leads-overview-card">
                <Card.Header>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: '#00d4ff' }} />
                    AI Lead Source Intelligence
                  </h5>
                  <small className="text-muted">Neural network-powered channel performance analysis</small>
                </Card.Header>
                <Card.Body>
                  <LeadSources leads={leads || []} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* AI Chatbot Assistant - Now available globally via GlobalDashboardAI component */}
        </div>
        )
      )}
      {!isAuthorized && <Backdrop />}
    </>
  );
};

export default Crm;
